import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, MagnifyingGlass, SquaresFour, ListBullets, MapTrifold, Bed, Bathtub, Ruler, MapPin,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { eur, price, fdate } from "@/lib/format";
import { PageHeader, StatusBadge, EmptyState, DemoBadge } from "@/components/common";
import PropertyForm from "@/components/PropertyForm";
import PropertyMap from "@/components/PropertyMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useTranslation } from 'react-i18next'
import { localizedField } from "@/lib/localized";

function PropertyCard({ p, onClick }) {
  const { t, i18n } = useTranslation()
  return (
    <div
      onClick={onClick}
      data-testid={`property-card-${p.id}`}
      className="group bg-white border border-slate-200 rounded-sm overflow-hidden cursor-pointer shadow-[0_8px_30px_rgba(11,37,69,0.05)] hover:-translate-y-[3px] hover:shadow-[0_12px_40px_rgba(11,37,69,0.1)] transition-transform"
    >
      <div className="relative h-52 overflow-hidden bg-slate-100">
        {p.cover_image ? (
          <img src={p.cover_image} alt={localizedField(p, "title", i18n)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><MapPin size={40} /></div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <StatusBadge status={p.status} />
          {p.is_demo && <DemoBadge />}
        </div>
        {p.visibility === "confidential" && (
          <span className="absolute top-3 right-3 bg-[#0B2545] text-white text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm">{t('confidential', 'Confidential')}</span>
        )}
      </div>
      <div className="p-5">
        <div className="text-xs text-slate-400 font-semibold">{t('referenceCategory', '{{reference}} · {{category}}', { reference: p.reference, category: p.category })}</div>
        <h3 className="font-serif text-lg text-slate-900 mt-1 line-clamp-1">{localizedField(p, "title", i18n)}</h3>
        <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
          <MapPin size={14} /> {p.city}{p.district ? t('district', ', {{district}}', { district: p.district }) : ""}
        </div>
        <div className="font-serif text-xl text-[#0B2545] mt-3">{price(p.asking_price)}</div>
        <div className="flex items-center gap-4 text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
          {p.bedrooms ? <span className="flex items-center gap-1"><Bed size={14} /> {p.bedrooms}</span> : null}
          {p.bathrooms ? <span className="flex items-center gap-1"><Bathtub size={14} /> {p.bathrooms}</span> : null}
          {p.gross_area ? <span className="flex items-center gap-1"><Ruler size={14} />{t('gross_areaM', '{{gross_area}} m²', { gross_area: p.gross_area })}</span> : null}
        </div>
      </div>
    </div>
  );
}

export default function Properties() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("grid");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [category, setCategory] = useState("all");
  const [showForm, setShowForm] = useState(false);

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: async () => (await api.get("/meta")).data });

  useEffect(() => {
    if (params.get("new") === "1") { setShowForm(true); setParams({}); }
  }, [params, setParams]);

  const filters = {};
  if (status !== "all") filters.status = status;
  if (category !== "all") filters.category = category;
  if (q) filters.q = q;

  const { data: properties = [], isLoading } = useQuery({
    queryKey: ["properties", filters],
    queryFn: async () => (await api.get("/properties", { params: filters })).data,
  });

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/properties", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["properties"] }); setShowForm(false); toast.success("Property created"); },
    onError: () => toast.error("Failed to create property"),
  });

  return (
    <div>
      <PageHeader
        title="Properties"
        subtitle={t('lengthPropertiesInPortfolio', '{{length}} properties in portfolio', { length: properties.length })}
        actions={
          <Button data-testid="add-property-btn" onClick={() => setShowForm(true)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
            <Plus size={16} className="mr-1.5" /> {t('addProperty', 'Add Property')}
          </Button>
        }
      />

      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="property-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('searchByTitleReferenceCity', 'Search by title, reference, city…')} className="pl-10 rounded-sm bg-white" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status" className="w-full lg:w-48 rounded-sm bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">{t('allStatuses', 'All statuses')}</SelectItem>
            {meta?.property_statuses?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="filter-category" className="w-full lg:w-48 rounded-sm bg-white"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-white max-h-64">
            <SelectItem value="all">{t('allCategories', 'All categories')}</SelectItem>
            {meta?.property_categories?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex border border-slate-200 rounded-sm bg-white">
          {[["grid", SquaresFour], ["list", ListBullets], ["map", MapTrifold]].map(([v, Icon]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
              className={`px-3 py-2 transition-colors ${view === v ? "bg-[#0B2545] text-white" : "text-slate-500 hover:bg-slate-50"}`}>
              <Icon size={18} />
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>
      ) : properties.length === 0 ? (
        <EmptyState icon={MapPin} title={t('noPropertiesFound', 'No properties found')} subtitle="Adjust filters or add your first property." />
      ) : view === "map" ? (
        <PropertyMap properties={properties} onSelect={(p) => navigate(`/properties/${p.id}`)} />
      ) : view === "list" ? (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">{t('reference', 'Reference')}</th>
                <th className="text-left px-4 py-3">{t('title2', 'Title')}</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">{t('location', 'Location')}</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">{t('category', 'Category')}</th>
                <th className="text-left px-4 py-3">{t('price', 'Price')}</th>
                <th className="text-left px-4 py-3">{t('status', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p) => (
                <tr key={p.id} onClick={() => navigate(`/properties/${p.id}`)} data-testid={`property-row-${p.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-3 font-medium text-slate-600">{p.reference}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{localizedField(p, "title", i18n)}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-500">{t('cityDistrict', '{{city}}, {{district}}', { city: p.city, district: p.district })}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-500">{p.category}</td>
                  <td className="px-4 py-3 text-[#0B2545] font-medium">{price(p.asking_price)}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {properties.map((p) => <PropertyCard key={p.id} p={p} onClick={() => navigate(`/properties/${p.id}`)} />)}
        </div>
      )}

      {showForm && (
        <PropertyForm open={showForm} onOpenChange={setShowForm} meta={meta} onSubmit={(payload) => create.mutate(payload)} />
      )}
    </div>
  );
}
